import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Eye, EyeOff, Mail, KeyRound, ArrowLeft, Loader2 } from "lucide-react";
import { toast } from "sonner";
import StoreLayout from "../components/StoreLayout";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";

const BG =
  "https://images.unsplash.com/photo-1761084688828-c53ddc918307?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwzfHxzdHJlZXR3ZWFyJTIwZmFzaGlvbiUyMG1vZGVsJTIwbmVvbiUyMGRhcmslMjB1cmJhbnxlbnwwfHx8fDE3Nzk1MTk0Njd8MA&ixlib=rb-4.1.0&q=85";

// Countdown from 30s so users can request a new OTP after that window
const RESEND_COOLDOWN = 30;

export default function Login() {
  const [mode, setMode] = useState("otp"); // "password" | "otp"
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);

  // OTP state
  const [otpStep, setOtpStep] = useState("email"); // "email" | "code"
  const [code, setCode] = useState("");
  const [cooldown, setCooldown] = useState(0);
  const codeInputRef = useRef(null);

  const { login, loginWithToken } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next") || "/";

  // Cooldown timer
  useEffect(() => {
    if (cooldown <= 0) return;
    const id = setInterval(() => setCooldown((c) => (c > 0 ? c - 1 : 0)), 1000);
    return () => clearInterval(id);
  }, [cooldown]);

  // Auto-focus the code input when we advance to step 2
  useEffect(() => {
    if (mode === "otp" && otpStep === "code") {
      setTimeout(() => codeInputRef.current?.focus(), 60);
    }
  }, [mode, otpStep]);

  const goHomeOrAdmin = (u) => navigate(u.role === "admin" ? "/admin" : next);

  const submitPassword = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await login(email, password);
      toast.success("WELCOME BACK", { description: u.name });
      goHomeOrAdmin(u);
    } catch (err) {
      toast.error("LOGIN FAILED", { description: err?.response?.data?.detail || "Invalid credentials" });
    } finally {
      setBusy(false);
    }
  };

  const requestOtp = async (e) => {
    e?.preventDefault?.();
    if (!/^\S+@\S+\.\S+$/.test(email)) {
      toast.error("Enter a valid email first");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/otp/request", { email });
      setOtpStep("code");
      setCode("");
      setCooldown(RESEND_COOLDOWN);
      toast.success("CODE SENT", { description: `Check ${email} — expires in 10 minutes.` });
    } catch (err) {
      const status = err?.response?.status;
      if (status === 429) {
        toast.error("TOO MANY REQUESTS", { description: "Try again in a few minutes." });
      } else {
        toast.error("Couldn't send", { description: err?.response?.data?.detail || "" });
      }
    } finally {
      setBusy(false);
    }
  };

  const verifyOtp = async (e) => {
    e?.preventDefault?.();
    if (!/^\d{6}$/.test(code)) {
      toast.error("Enter the 6-digit code from your email");
      return;
    }
    setBusy(true);
    try {
      const res = await api.post("/auth/otp/verify", { email, code });
      const u = loginWithToken(res.data.token, res.data.user);
      toast.success("SIGNED IN", { description: u.name || u.email });
      goHomeOrAdmin(u);
    } catch (err) {
      toast.error("VERIFY FAILED", { description: err?.response?.data?.detail || "Invalid code" });
    } finally {
      setBusy(false);
    }
  };

  const switchMode = (m) => {
    setMode(m);
    setOtpStep("email");
    setCode("");
    setPassword("");
  };

  return (
    <StoreLayout>
      <div className="grid md:grid-cols-2 min-h-[80vh]">
        <div className="relative hidden md:block">
          <img src={BG} alt="" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-br from-black/40 to-black/90" />
          <div className="absolute bottom-10 left-10 right-10">
            <p className="label-mono text-[#CFF048]">// MEMBER ACCESS</p>
            <h2 className="hero-title text-5xl uppercase mt-2">Cult<br/>Members<br/>Only.</h2>
          </div>
        </div>

        <div className="flex items-center justify-center p-8 md:p-16">
          <div className="w-full max-w-md" data-testid="login-form">
            <p className="label-mono text-[#71717A]">// AUTH</p>
            <h1 className="font-display font-black text-4xl md:text-5xl uppercase mt-3">Sign In</h1>
            <p className="text-[#A1A1AA] mt-2">Welcome back, cult member.</p>

            {/* Mode toggle */}
            <div className="mt-6 grid grid-cols-2 border border-[#27272A]" data-testid="login-mode-toggle">
              <button
                type="button"
                onClick={() => switchMode("password")}
                className={`label-mono py-3 flex items-center justify-center gap-2 transition-colors ${
                  mode === "password" ? "bg-[#CFF048] text-black" : "text-[#71717A] hover:text-white"
                }`}
                data-testid="tab-password"
              >
                <KeyRound size={13} /> PASSWORD
              </button>
              <button
                type="button"
                onClick={() => switchMode("otp")}
                className={`label-mono py-3 flex items-center justify-center gap-2 transition-colors ${
                  mode === "otp" ? "bg-[#CFF048] text-black" : "text-[#71717A] hover:text-white"
                }`}
                data-testid="tab-otp"
              >
                <Mail size={13} /> EMAIL OTP
              </button>
            </div>

            {/* PASSWORD MODE */}
            {mode === "password" && (
              <form onSubmit={submitPassword} className="mt-6 space-y-4" data-testid="password-form">
                <input
                  type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                  placeholder="EMAIL ADDRESS"
                  className="cult-input"
                  data-testid="login-email"
                />
                <div className="relative">
                  <input
                    type={showPw ? "text" : "password"} required value={password} onChange={(e) => setPassword(e.target.value)}
                    placeholder="PASSWORD"
                    className="cult-input pr-12"
                    data-testid="login-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPw(!showPw)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#71717A] hover:text-[#CFF048]"
                    data-testid="toggle-password"
                    aria-label={showPw ? "Hide password" : "Show password"}
                  >
                    {showPw ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                <button disabled={busy} type="submit" className="btn-neon w-full" data-testid="login-submit">
                  {busy ? "SIGNING IN..." : "SIGN IN"}
                </button>
              </form>
            )}

            {/* OTP MODE — step 1: email */}
            {mode === "otp" && otpStep === "email" && (
              <form onSubmit={requestOtp} className="mt-6 space-y-4" data-testid="otp-email-form">
                <p className="label-mono text-[#71717A] text-[10px] leading-relaxed">
                  // NO PASSWORD? WE&apos;LL EMAIL YOU A 6-DIGIT CODE. VALID FOR 10 MIN.
                </p>
                <input
                  type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                  placeholder="REGISTERED EMAIL ADDRESS"
                  className="cult-input"
                  data-testid="otp-email"
                />
                <button
                  disabled={busy}
                  type="submit"
                  className="btn-neon w-full flex items-center justify-center gap-2 disabled:opacity-50"
                  data-testid="request-otp-btn"
                >
                  {busy ? <Loader2 size={16} className="animate-spin" /> : <Mail size={16} />}
                  {busy ? "SENDING..." : "EMAIL ME A CODE"}
                </button>
              </form>
            )}

            {/* OTP MODE — step 2: code */}
            {mode === "otp" && otpStep === "code" && (
              <form onSubmit={verifyOtp} className="mt-6 space-y-4" data-testid="otp-code-form">
                <button
                  type="button"
                  onClick={() => { setOtpStep("email"); setCode(""); }}
                  className="label-mono text-[#71717A] hover:text-[#CFF048] flex items-center gap-1"
                  data-testid="otp-back-btn"
                >
                  <ArrowLeft size={12} /> WRONG EMAIL? GO BACK
                </button>
                <p className="text-[#A1A1AA] text-sm">
                  Sent a 6-digit code to <span className="text-[#CFF048]">{email}</span>. Enter it below.
                </p>
                <input
                  ref={codeInputRef}
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  pattern="[0-9]*"
                  maxLength={6}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  placeholder="6-DIGIT CODE"
                  className="cult-input text-center tracking-[0.5em] font-mono text-2xl"
                  data-testid="otp-code"
                />
                <button
                  disabled={busy || code.length !== 6}
                  type="submit"
                  className="btn-neon w-full flex items-center justify-center gap-2 disabled:opacity-50"
                  data-testid="verify-otp-btn"
                >
                  {busy ? <Loader2 size={16} className="animate-spin" /> : null}
                  {busy ? "VERIFYING..." : "VERIFY & SIGN IN"}
                </button>
                <div className="text-center">
                  {cooldown > 0 ? (
                    <p className="label-mono text-[#71717A]" data-testid="otp-cooldown">
                      // RESEND IN {cooldown}s
                    </p>
                  ) : (
                    <button
                      type="button"
                      onClick={requestOtp}
                      className="label-mono text-[#CFF048] hover:underline"
                      data-testid="otp-resend-btn"
                    >
                      // RESEND CODE
                    </button>
                  )}
                </div>
              </form>
            )}

            <p className="label-mono text-[#71717A] mt-6 text-center">
              NEW HERE?{" "}
              <Link
                to={`/signup${next !== "/" ? `?next=${next}` : ""}`}
                className="text-[#CFF048] hover:underline"
                data-testid="signup-link"
              >
                CREATE ACCOUNT
              </Link>
            </p>
          </div>
        </div>
      </div>
    </StoreLayout>
  );
}
